var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { exhaustiveMatchingGuard } from "../../helpers/typechecks/exhaustiveMatchingGuard";
import Icon from "@ant-design/icons/lib/components/Icon";
import { CheckmarkPath } from "./IconPaths/CheckmarkPath";
import { CrossPath } from "./IconPaths/CrossPath";
import { ExclamationPath } from "./IconPaths/ExclamationPath";
import { InfoPath } from "./IconPaths/InfoPath";
import useToken from "antd/lib/theme/useToken";
/**
 * Displaying an icon based on its severity (success, info, warning or error).
 * Use these icons to indicate the severity of a message or alert.
 * The icons have great contrast, for increased accessibility.
 */
export function SeverityIcon(_a) {
    var { severity, size, alt } = _a, restProps = __rest(_a, ["severity", "size", "alt"]);
    switch (severity) {
        case "error":
            return _jsx(ErrorIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "information":
            return _jsx(InformationIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "warning":
            return _jsx(WarningIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "success":
            return _jsx(SuccessIcon, Object.assign({ size: size, alt: alt }, restProps));
        default:
            return exhaustiveMatchingGuard(severity);
    }
}
/**
 * Renders an error icon.
 */
const ErrorIcon = (_a) => {
    var props = __rest(_a, []);
    const [, token] = useToken();
    return (_jsx(IconWrapper, Object.assign({ fill: token.red7 }, props, { children: _jsx(CrossPath, { color: token.red9 }) })));
};
/**
 * Renders an information icon.
 */
const InformationIcon = (_a) => {
    var props = __rest(_a, []);
    const [, token] = useToken();
    return (_jsx(IconWrapper, Object.assign({ fill: token.blue6 }, props, { children: _jsx(InfoPath, { color: token.blue9 }) })));
};
/**
 * Renders a warning icon.
 */
const WarningIcon = (_a) => {
    var props = __rest(_a, []);
    const [, token] = useToken();
    return (_jsx(IconWrapper, Object.assign({ fill: token.yellow6 }, props, { children: _jsx(ExclamationPath, { color: token.yellow9 }) })));
};
/**
 * Renders a success icon.
 */
const SuccessIcon = (_a) => {
    var props = __rest(_a, []);
    const [, token] = useToken();
    return (_jsx(IconWrapper, Object.assign({ fill: token.green6 }, props, { children: _jsx(CheckmarkPath, { color: token.green9 }) })));
};
const IconWrapper = (props) => (_jsx(Icon, Object.assign({ "aria-label": props.alt, component: OutlineCircle(props), style: {
        height: props.size,
        width: props.size
    } }, props)));
function OutlineCircle({ children, fill }) {
    return (props) => (_jsxs("svg", Object.assign({}, props, { width: "100%", height: "100%", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M12 1.52344C6.20156 1.52344 1.5 6.225 1.5 12.0234C1.5 17.8219 6.20156 22.5234 12 22.5234C17.7984 22.5234 22.5 17.8219 22.5 12.0234C22.5 6.225 17.7984 1.52344 12 1.52344ZM12 20.7422C7.18594 20.7422 3.28125 16.8375 3.28125 12.0234C3.28125 7.20938 7.18594 3.30469 12 3.30469C16.8141 3.30469 20.7188 7.20938 20.7188 12.0234C20.7188 16.8375 16.8141 20.7422 12 20.7422Z", fill: fill }), children] })));
}
